/**
 * ================================================================
 * API SERVICE: PasswordService
 * ================================================================
 *
 * @service          passwordApi
 * @description      RTK Query service for change/set/forgot password flows
 * @baseUrl          /user/* (prefixed by VITE_API_BASE_URL or default)
 *
 * @endpoints
 * - POST   /user/change-password?source=ap - Protected change password
 * - POST   /user/set-password               - Public set password
 * - POST   /user/forgot-password?process=.. - Public forgot steps
 *
 * @authentication  Protected endpoint includes auth-token via baseQuery
 * @caching         No caching, mutation-only operations
 * @errorHandling   Uses global createApiErrorTransformer
 *
 * @types
 * - ChangePasswordRequest/Response
 * - SetPasswordRequest/Response
 * - ForgotPassword types
 * ================================================================
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiErrorTransformer, createBaseQuery, handleApiSuccess } from '@/lib/api/baseQuery';
import type {
  ChangePasswordRequest,
  ChangePasswordResponse,
  ForgotPasswordBase,
  ForgotPasswordResponse,
  ForgotPasswordVerify,
  SetPasswordRequest,
  SetPasswordResponse,
} from '@/types/auth/password';

export const passwordApi = createApi({
  reducerPath: 'passwordApi',
  baseQuery: createBaseQuery(
    import.meta.env.VITE_API_BASE_URL || 'https://web.arhamshare.com/apb/v2',
    'auth-token',
  ),
  endpoints: (builder) => ({
    // Change Password (protected)
    changePassword: builder.mutation<ChangePasswordResponse, ChangePasswordRequest>({
      query: (body) => ({
        url: '/user/change-password',
        method: 'POST',
        params: { source: 'ap' },
        body,
      }),
      transformErrorResponse: createApiErrorTransformer('PasswordChange', true),
      async onQueryStarted(_, { queryFulfilled }) {
        const { data } = await queryFulfilled;
        handleApiSuccess('PasswordChange', data?.message || 'Password changed successfully.', true);
      },
    }),

    // Set Password (public)
    setPassword: builder.mutation<SetPasswordResponse, SetPasswordRequest>({
      query: (body) => ({
        url: '/user/set-password',
        method: 'POST',
        headers: { authToken: sessionStorage.getItem('pwdToken') || '' },
        body,
      }),
      transformErrorResponse: createApiErrorTransformer('PasswordSet', true),
      async onQueryStarted(_, { queryFulfilled }) {
        const { data } = await queryFulfilled;
        handleApiSuccess('PasswordSet', data?.message || 'Password reset successfully.', true);
      },
    }),

    // Forgot Password (public) - send OTP
    forgotPasswordSendOtp: builder.mutation<ForgotPasswordResponse, ForgotPasswordBase>({
      query: (body) => ({
        url: '/user/forgot-password',
        method: 'POST',
        params: { process: 'send_otp' },
        body,
      }),
      transformErrorResponse: createApiErrorTransformer('ForgotSendOtp', true),
      async onQueryStarted(_, { queryFulfilled }) {
        const { data } = await queryFulfilled;
        handleApiSuccess('ForgotSendOtp', data?.message || 'OTP sent successfully.', true);
      },
    }),

    // Forgot Password (public) - verify OTP
    forgotPasswordVerifyOtp: builder.mutation<ForgotPasswordResponse, ForgotPasswordVerify>({
      query: (body) => ({
        url: '/user/forgot-password',
        method: 'POST',
        params: { process: 'verify_otp' },
        body,
      }),
      transformErrorResponse: createApiErrorTransformer('ForgotVerifyOtp', true),
      async onQueryStarted(_, { queryFulfilled }) {
        const { data } = await queryFulfilled;
        handleApiSuccess('ForgotVerifyOtp', data?.message || 'OTP verified successfully.', true);
      },
    }),

    // Forgot Password (public) - resend OTP
    forgotPasswordResendOtp: builder.mutation<ForgotPasswordResponse, ForgotPasswordBase>({
      query: (body) => ({
        url: '/user/forgot-password',
        method: 'POST',
        params: { process: 'resend_otp' },
        body,
      }),
      transformErrorResponse: createApiErrorTransformer('ForgotResendOtp', true),
      async onQueryStarted(_, { queryFulfilled }) {
        const { data } = await queryFulfilled;
        handleApiSuccess('ForgotResendOtp', data?.message || 'OTP resent successfully.', true);
      },
    }),
  }),
});

export const {
  useChangePasswordMutation,
  useSetPasswordMutation,
  useForgotPasswordSendOtpMutation,
  useForgotPasswordVerifyOtpMutation,
  useForgotPasswordResendOtpMutation,
} = passwordApi;

/**
 * ================================================================
 * END OF SERVICE: PasswordService
 * ================================================================
 */
